import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

// Breakpoints from MUI theme
const BREAKPOINTS = {
  xs: 0,
  sm: 600,
  md: 900,
  lg: 1200,
  xl: 1536,
};

const getBreakpointColor = (breakpoint, theme) => {
  const colors = {
    xs: { bg: theme.palette.error.lighter, text: theme.palette.error.darker, border: theme.palette.error.light },
    sm: { bg: theme.palette.warning.lighter, text: theme.palette.warning.darker, border: theme.palette.warning.light },
    md: { bg: theme.palette.info.lighter, text: theme.palette.info.darker, border: theme.palette.info.light },
    lg: { bg: theme.palette.success.lighter, text: theme.palette.success.darker, border: theme.palette.success.light },
    xl: { bg: theme.palette.primary.lighter, text: theme.palette.primary.darker, border: theme.palette.primary.light },
  };
  return colors[breakpoint] || colors.xs;
};

export function ScreenSizeIndicator() {
  const theme = useTheme();

  // Call hooks at the top level to avoid "hook order" errors
  const isXL = useMediaQuery(theme.breakpoints.up('xl'));
  const isLG = useMediaQuery(theme.breakpoints.up('lg'));
  const isMD = useMediaQuery(theme.breakpoints.up('md'));
  const isSM = useMediaQuery(theme.breakpoints.up('sm'));

  // Determine the current breakpoint
  const currentBreakpoint = isXL ? 'xl' : isLG ? 'lg' : isMD ? 'md' : isSM ? 'sm' : 'xs';
  const colors = getBreakpointColor(currentBreakpoint, theme);

  const renderTooltip = () => (
    <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Typography variant="caption" sx={{ fontWeight: 700 }}>
        Current Breakpoint: {currentBreakpoint.toUpperCase()}
      </Typography>
      <Typography variant="caption">
        Width: {BREAKPOINTS[currentBreakpoint]}px+
      </Typography>
      <Typography variant="caption" sx={{ mt: 1, pt: 1, borderTop: `1px solid ${theme.palette.divider}` }}>
        <strong>Breakpoints:</strong>
      </Typography>
      {Object.entries(BREAKPOINTS).map(([key, value]) => (
        <Typography
          key={key}
          variant="caption"
          sx={{
            fontWeight: currentBreakpoint === key ? 700 : 400,
            color: currentBreakpoint === key ? theme.palette.primary.main : 'inherit',
          }}
        >
          {key}: {value}px
        </Typography>
      ))}
    </Box>
  );

  return (
    <Tooltip title={renderTooltip()} placement="bottom">
      <Box
        sx={{
          px: 1,
          py: 0.5,
          borderRadius: 1,
          bgcolor: colors.bg,
          color: colors.text,
          border: `1px solid ${colors.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          minWidth: 32,
          height: 32,
          fontSize: '0.75rem',
          fontWeight: 700,
        }}
      >
        {currentBreakpoint.toUpperCase()}
      </Box>
    </Tooltip>
  );
}
